/**
 * Global SmartAdd Jobs Store
 *
 * Tracks active background SmartAdd jobs with real-time Firestore subscriptions.
 * Provides persistent notifications when jobs complete.
 */

import { create } from 'zustand';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logger } from '@/lib/logger';
import type { SmartAddJob } from '../types';

export interface TrackedJob {
  uploadId: string;
  teamId: string;
  status: SmartAddJob['status'] | 'pending_creation';
  fileName?: string;
  updatedAt: Date;
  error?: SmartAddJob['error'];
  trackingStartedAt: Date; // When we started tracking (for grace period)
}

export interface CompletedNotification {
  uploadId: string;
  teamId: string;
  status: 'review' | 'failed';
  fileName?: string;
  error?: SmartAddJob['error'];
  timestamp: Date;
}

interface SmartAddJobsStore {
  // Active jobs being tracked
  activeJobs: Map<string, TrackedJob>;

  // Completed jobs awaiting user acknowledgment
  notifications: CompletedNotification[];

  // Firestore unsubscribe functions
  unsubscribers: Map<string, Unsubscribe>;

  // Actions
  trackJob: (uploadId: string, teamId: string, fileName?: string) => void;
  stopTracking: (uploadId: string) => void;
  dismissNotification: (uploadId: string) => void;
  clearAllNotifications: () => void;

  // Internal update handler
  _updateJob: (uploadId: string, job: SmartAddJob) => void;
}

export const useSmartAddJobsStore = create<SmartAddJobsStore>((set, get) => ({
  activeJobs: new Map(),
  notifications: [],
  unsubscribers: new Map(),

  trackJob: (uploadId: string, teamId: string, fileName?: string) => {
    const { unsubscribers } = get();

    // Already tracking this job
    if (unsubscribers.has(uploadId)) {
      logger.info('[SmartAddJobsStore] Already tracking job', { uploadId });
      return;
    }

    logger.info('[SmartAddJobsStore] Starting to track job', { uploadId, teamId, fileName });

    const jobRef = doc(db, 'teams', teamId, 'smartAddJobs', uploadId);

    const unsubscribe = onSnapshot(
      jobRef,
      (snapshot) => {
        const { activeJobs } = get();
        const trackedJob = activeJobs.get(uploadId);

        if (!snapshot.exists()) {
          // GRACE PERIOD: Don't show error if doc is expected to be created soon
          const now = Date.now();
          const trackingStartedAt = trackedJob?.trackingStartedAt?.getTime() || now;
          const elapsedMs = now - trackingStartedAt;
          const GRACE_PERIOD_MS = 5000; // 5 seconds

          if (elapsedMs < GRACE_PERIOD_MS) {
            // Still within grace period - treat as pending creation
            logger.info('[SmartAddJobsStore] Job doc not found yet (within grace period)', {
              uploadId,
              elapsedMs,
              remainingMs: GRACE_PERIOD_MS - elapsedMs,
            });

            // Update status to pending_creation if not already
            if (trackedJob && trackedJob.status !== 'pending_creation') {
              set((state) => ({
                activeJobs: new Map(state.activeJobs).set(uploadId, {
                  ...trackedJob,
                  status: 'pending_creation',
                  updatedAt: new Date(),
                }),
              }));
            }
          } else {
            // Grace period expired - this is a real error
            logger.warn('[SmartAddJobsStore] Job document not found (grace period expired)', {
              uploadId,
              elapsedMs,
            });

            // Update to failed state
            if (trackedJob) {
              set((state) => ({
                activeJobs: new Map(state.activeJobs).set(uploadId, {
                  ...trackedJob,
                  status: 'failed',
                  error: {
                    code: 'NOT_FOUND',
                    message: 'De analyse is niet gevonden of verlopen',
                  },
                  updatedAt: new Date(),
                }),
              }));
            }
          }
          return;
        }

        const job = snapshot.data() as SmartAddJob;
        get()._updateJob(uploadId, job);
      },
      (error) => {
        logger.error('[SmartAddJobsStore] Subscription error', { uploadId, error });
        get().stopTracking(uploadId);
      }
    );

    const now = new Date();
    set((state) => ({
      unsubscribers: new Map(state.unsubscribers).set(uploadId, unsubscribe),
      activeJobs: new Map(state.activeJobs).set(uploadId, {
        uploadId,
        teamId,
        status: 'created', // Job doc should exist immediately (created by initiateSmartAdd)
        fileName,
        updatedAt: now,
        trackingStartedAt: now, // Track when we started for timeout
      }),
    }));
  },

  stopTracking: (uploadId: string) => {
    const { unsubscribers } = get();
    const unsubscribe = unsubscribers.get(uploadId);

    if (unsubscribe) {
      unsubscribe();
      logger.info('[SmartAddJobsStore] Stopped tracking job', { uploadId });
    }

    set((state) => {
      const newUnsubscribers = new Map(state.unsubscribers);
      newUnsubscribers.delete(uploadId);

      const newActiveJobs = new Map(state.activeJobs);
      newActiveJobs.delete(uploadId);

      return {
        unsubscribers: newUnsubscribers,
        activeJobs: newActiveJobs,
      };
    });
  },

  dismissNotification: (uploadId: string) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.uploadId !== uploadId),
    }));
    logger.info('[SmartAddJobsStore] Dismissed notification', { uploadId });
  },

  clearAllNotifications: () => {
    set({ notifications: [] });
    logger.info('[SmartAddJobsStore] Cleared all notifications');
  },

  _updateJob: (uploadId: string, job: SmartAddJob) => {
    const { activeJobs } = get();
    const trackedJob = activeJobs.get(uploadId);

    if (!trackedJob) return;

    const newStatus = job.status;
    const oldStatus = trackedJob.status;

    logger.info('[SmartAddJobsStore] Job status update', {
      uploadId,
      oldStatus,
      newStatus,
    });

    // Update active job - get fileName from files array
    const jobFileName = job.files?.[0]?.fileName;
    const updatedJob: TrackedJob = {
      ...trackedJob,
      status: newStatus,
      fileName: jobFileName || trackedJob.fileName,
      updatedAt: new Date(),
      error: job.error,
    };

    set((state) => ({
      activeJobs: new Map(state.activeJobs).set(uploadId, updatedJob),
    }));

    // If job completed (review/failed/committed/cancelled), create notification and stop tracking
    if (newStatus === 'review' || newStatus === 'failed') {
      // Only create notification if status changed (not on initial load)
      if (oldStatus !== newStatus && (oldStatus === 'queued' || oldStatus === 'analyzing')) {
        const notification: CompletedNotification = {
          uploadId,
          teamId: trackedJob.teamId,
          status: newStatus,
          fileName: jobFileName || trackedJob.fileName,
          error: job.error,
          timestamp: new Date(),
        };

        set((state) => ({
          notifications: [...state.notifications, notification],
        }));

        logger.info('[SmartAddJobsStore] Created notification', {
          uploadId,
          status: newStatus,
        });
      }

      // Stop tracking after a delay to allow UI to show final state
      setTimeout(() => {
        get().stopTracking(uploadId);
      }, 1000);
    } else if (newStatus === 'committed') {
      // Job was committed - just stop tracking, no notification needed
      get().stopTracking(uploadId);
    } else if (newStatus === 'cancelled') {
      // UX: Job was cancelled by user - stop tracking immediately, no notification
      get().stopTracking(uploadId);
      logger.info('[SmartAddJobsStore] Job cancelled - stopped tracking without notification', {
        uploadId,
      });
    }
  },
}));
